<template>
    <transition name="slide-fade">
        <div v-if="isVisible" :class="['cookie-consent-wrapper', { 'dark': isDarkMode }]">
            <div class="cookie-consent-card">
                <!-- Mode: Main Banner -->
                <div v-if="mode === 'banner'" class="cookie-content-col">
                    <div class="cookie-header-row">
                        <!-- Animated Cookie Icon Container -->
                        <div class="cookie-icon-wrapper">
                            <svg xmlns="http://www.w3.org/2000/svg" class="cookie-icon" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                                <circle cx="8.5" cy="8.5" r="1" fill="currentColor" />
                                <circle cx="16" cy="15.5" r="1" fill="currentColor" />
                                <circle cx="12" cy="12" r="1" fill="currentColor" />
                                <circle cx="11" cy="17" r="1" fill="currentColor" />
                                <circle cx="7" cy="14" r="1" fill="currentColor" />
                            </svg>
                        </div>
                        <div class="cookie-text-col">
                            <h3 class="cookie-title">
                                {{ $t('cookie.title') || 'We value your privacy' }}
                            </h3>
                            <p class="cookie-description">
                                {{ $t('cookie.description') }}
                            </p>
                        </div>
                    </div>

                    <div class="cookie-divider">
                        <a href="/privacy-Policy" class="cookie-link">
                            {{ $t('cookie.privacyLabel') || 'Privacy Policy' }}
                        </a>
                        <div class="cookie-actions">
                            <!-- Customize Button -->
                            <button @click="mode = 'customize'" class="cookie-btn-secondary">
                                {{ $t('cookie.customize') || 'Customize' }}
                            </button>

                            <!-- Decline Button -->
                            <button @click="declineCookies" class="cookie-btn-secondary">
                                {{ $t('cookie.decline') || 'Decline' }}
                            </button>

                            <!-- Accept All Button -->
                            <button @click="acceptAllCookies" class="cookie-btn-primary">
                                {{ $t('cookie.accept') || 'Accept All' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Mode: Custom Settings -->
                <div v-else-if="mode === 'customize'" class="cookie-content-col">
                    <div class="cookie-divider customize-header">
                        <h3 class="cookie-title">
                            {{ $t('cookie.customizeTitle') || 'Customize Cookies' }}
                        </h3>
                        <button @click="mode = 'banner'" class="cookie-btn-back">
                            <svg xmlns="http://www.w3.org/2000/svg" class="back-icon" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor" stroke-width="2.5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            {{ $t('cookie.back') || 'Back' }}
                        </button>
                    </div>

                    <!-- Cookie Preferences List -->
                    <div class="cookie-preferences-list">
                        <!-- Necessary Cookies -->
                        <div class="cookie-pref-item">
                            <div class="cookie-pref-text">
                                <h4 class="cookie-item-title">
                                    {{ $t('cookie.necessaryTitle') || 'Strictly Necessary' }}
                                </h4>
                                <p class="cookie-item-desc">
                                    {{ $t('cookie.necessaryDesc') || 'Required for the website to function properly.' }}
                                </p>
                            </div>
                            <div class="cookie-switch cookie-switch-disabled">
                                <div class="cookie-switch-thumb disabled"></div>
                            </div>
                        </div>

                        <!-- Analytics Cookies -->
                        <div class="cookie-pref-item">
                            <div class="cookie-pref-text">
                                <h4 class="cookie-item-title">
                                    {{ $t('cookie.analyticsTitle') }}
                                </h4>
                                <p class="cookie-item-desc">
                                    {{ $t('cookie.analyticsDesc') }}
                                </p>
                            </div>
                            <button @click="prefs.analytics = !prefs.analytics" class="cookie-switch"
                                :class="{ 'active': prefs.analytics }">
                                <div class="cookie-switch-thumb" :class="{ 'active': prefs.analytics }"></div>
                            </button>
                        </div>

                        <!-- Marketing Cookies -->
                        <div class="cookie-pref-item">
                            <div class="cookie-pref-text">
                                <h4 class="cookie-item-title">
                                    {{ $t('cookie.marketingTitle') }}
                                </h4>
                                <p class="cookie-item-desc">
                                    {{ $t('cookie.marketingDesc') }}
                                </p>
                            </div>
                            <button @click="prefs.marketing = !prefs.marketing" class="cookie-switch"
                                :class="{ 'active': prefs.marketing }">
                                <div class="cookie-switch-thumb" :class="{ 'active': prefs.marketing }"></div>
                            </button>
                        </div>
                    </div>

                    <div class="cookie-divider customize-actions-row">
                        <div class="cookie-actions customize-actions">
                            <button @click="savePreferences" class="cookie-btn-save">
                                {{ $t('cookie.save') || 'Save Preferences' }}
                            </button>
                            <button @click="acceptAllCookies" class="cookie-btn-primary">
                                {{ $t('cookie.accept') || 'Accept All' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'CookieConsent',
    data() {
        return {
            isVisible: false,
            mode: 'banner', // 'banner' | 'customize'
            prefs: {
                necessary: true,
                analytics: false,
                marketing: false
            }
        }
    },
    computed: {
        isDarkMode() {
            return !this.$store.state.userLogin;
        }
    },
    mounted() {
        const consent = localStorage.getItem('cookie-consent')
        if (!consent) {
            setTimeout(() => {
                this.isVisible = true
            }, 1200)
        } else {
            try {
                const parsed = JSON.parse(consent)
                if (typeof parsed === 'object') {
                    Object.assign(this.prefs, parsed)
                }
            } catch {
                if (consent === 'accepted') {
                    this.prefs.analytics = true
                    this.prefs.marketing = true
                }
            }
        }
    },
    methods: {
        acceptAllCookies() {
            this.prefs.analytics = true
            this.prefs.marketing = true
            localStorage.setItem('cookie-consent', JSON.stringify(this.prefs))
            this.isVisible = false
        },
        declineCookies() {
            this.prefs.analytics = false
            this.prefs.marketing = false
            localStorage.setItem('cookie-consent', JSON.stringify(this.prefs))
            this.isVisible = false
        },
        savePreferences() {
            localStorage.setItem('cookie-consent', JSON.stringify(this.prefs))
            this.isVisible = false
        }
    }
}
</script>

<style scoped>
/* Layout & Container */
.cookie-consent-wrapper {
    position: fixed;
    bottom: 1.5rem;
    left: 0;
    right: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    padding: 0 1rem;
    /* Full-width strip: let clicks beside the card reach the page */
    pointer-events: none;
}

.cookie-consent-card {
    pointer-events: auto;
    width: 100%;
    max-width: 60rem;
    padding: 1.5rem;
    border-radius: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    text-align: left;
    transition: all 0.3s ease-in-out;
    background-color: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.8);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
}

@media (min-width: 768px) {
    .cookie-consent-card {
        padding: 2rem;
        gap: 2rem;
    }
}

.cookie-content-col {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.cookie-header-row {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
}

.cookie-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    border-radius: 1rem;
    flex-shrink: 0;
    background-color: rgba(138, 67, 248, 0.1);
    color: #8a43f8;
}

.cookie-icon {
    width: 2.5rem;
    height: 2.5rem;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: .5;
    }
}

.cookie-text-col {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

/* Typography (Fixed Size) */
.cookie-consent-wrapper {
    font-family: var(--font-primary);
}

.cookie-title {
    color: #111827;
    font-family: var(--font-primary);
    font-size: 20px;
    line-height: 1.4;
    margin: 0;
    font-weight: normal;
}

.cookie-description {
    color: #4b5563;
    font-size: 16px;
    line-height: 1.6;
    margin: 0;
}

.cookie-divider {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(229, 231, 235, 0.8);
    width: 100%;
}

@media (min-width: 768px) {
    .cookie-divider {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

.cookie-link {
    color: #8a43f8;
    font-family: var(--font-primary);
    text-decoration: underline;
    font-size: 16px;
    font-weight: Bold;
}

.cookie-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
}

@media (min-width: 768px) {
    .cookie-actions {
        flex-direction: row;
        width: auto;
        margin-left: auto;
    }
}

/* Buttons (Scaled Up) */
button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
}

.cookie-btn-secondary,
.cookie-btn-primary,
.cookie-btn-save,
.cookie-btn-back {
    font-family: var(--font-primary);
    font-size: 16px;
    border-radius: 0.75rem;
    text-align: center;
    transition: all 0.2s ease;
    width: 100%;
    font-weight: normal;
}

@media (min-width: 768px) {

    .cookie-btn-secondary,
    .cookie-btn-primary,
    .cookie-btn-save,
    .cookie-btn-back {
        width: auto;
    }
}

.cookie-btn-secondary,
.cookie-btn-save {
    padding: 0.75rem 1.5rem;
    color: #4b5563;
    background-color: transparent;
}

.cookie-btn-secondary:hover,
.cookie-btn-save:hover {
    color: #111827;
    background-color: #f3f4f6;
}

.cookie-btn-primary {
    padding: 0.75rem 1.75rem;
    color: #ffffff;
    background-color: #8a43f8;
}

.cookie-btn-primary:hover {
    background-color: #712ae0;
    transform: scale(0.98);
}

.cookie-btn-primary:active,
.cookie-btn-save:active {
    transform: scale(0.95);
}

.cookie-btn-back {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    color: #6b7280;
    background: none;
}

.cookie-btn-back:hover {
    color: #111827;
}

.back-icon {
    width: 1.25rem;
    height: 1.25rem;
}

/* Customize Settings Mode */
.customize-header {
    border-top: none;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    padding-top: 0;
    padding-bottom: 1rem;
    flex-direction: row;
}

.cookie-preferences-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0.5rem 0;
    max-height: 400px;
    overflow-y: auto;
}

/* Custom scrollbar */
.cookie-preferences-list::-webkit-scrollbar {
    width: 6px;
}

.cookie-preferences-list::-webkit-scrollbar-track {
    background: transparent;
}

.cookie-preferences-list::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.3);
    border-radius: 10px;
}

.cookie-preferences-list::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.5);
}

.cookie-pref-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
}

.cookie-pref-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.cookie-item-title {
    color: #1f2937;
    font-family: var(--font-primary);
    font-size: 20px;
    margin: 0;
    font-weight: normal;
}

.cookie-item-desc {
    color: #6b7280;
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
}

/* Switches */
.cookie-switch {
    width: 3.5rem;
    height: 2rem;
    border-radius: 9999px;
    position: relative;
    transition: background-color 0.2s ease;
    flex-shrink: 0;
    background-color: #e5e7eb;
}

.cookie-switch.active {
    background-color: #8a43f8;
}

.cookie-switch-disabled {
    background-color: #8a43f8;
    opacity: 0.6;
    cursor: not-allowed;
}

.cookie-switch-thumb {
    width: 1.5rem;
    height: 1.5rem;
    background-color: #ffffff;
    border-radius: 9999px;
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.cookie-switch-thumb.active,
.cookie-switch-thumb.disabled {
    transform: translateX(1.5rem);
}

.cookie-btn-save {
    background-color: #f3f4f6;
    color: #374151;
}

.customize-actions-row {
    margin-top: 0.5rem;
}

.customize-actions {
    flex-direction: column;
}

@media (min-width: 768px) {
    .customize-actions {
        flex-direction: row;
        margin-left: auto;
    }
}

/* Dark mode */
.dark .cookie-consent-card {
    background-color: rgba(42, 51, 61, 0.98);
    border-color: rgba(74, 85, 104, 0.5);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
}

.dark .cookie-icon-wrapper {
    background-color: rgba(248, 69, 127, 0.1);
    color: #f8457f;
}

.dark .cookie-title,
.dark .cookie-item-title {
    color: #ffffff;
}

.dark .cookie-description,
.dark .cookie-item-desc {
    color: #9cb3c9;
}

.dark .cookie-divider {
    border-color: rgba(74, 85, 104, 0.5);
}

.dark .cookie-link {
    color: #f8457f;
}

.dark .cookie-btn-secondary {
    color: #8d96a1;
}

.dark .cookie-btn-secondary:hover {
    color: #ffffff;
    background-color: #2a333d;
}

.dark .cookie-btn-primary {
    color: #ffffff;
    background-color: #f8457f;
}

.dark .cookie-btn-primary:hover {
    background-color: #d13063;
}

.dark .cookie-btn-back {
    color: #8d96a1;
}

.dark .cookie-btn-back:hover {
    color: #ffffff;
}

.dark .cookie-switch {
    background-color: #2a333d;
}

.dark .cookie-switch.active,
.dark .cookie-switch-disabled {
    background-color: #f8457f;
}

.dark .cookie-btn-save {
    color: #ffffff;
    background-color: #2a333d;
}

.dark .cookie-btn-save:hover {
    background-color: #4a5568;
}

/* Transitions & Animations */
.slide-fade-enter-active {
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
    transition: all 0.4s cubic-bezier(0.7, 0, 0.84, 0);
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(30px) scale(0.95);
    opacity: 0;
}
</style>
